import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  PolygonData,
  PolygonOverlay,
} from '@/components/ui/polygon/PolygonOverlay';
import { Device, Media } from '@/generated/graphql';
import { useFetchMedia } from '@/hooks/useFetchMedia';
import { MediaKind } from '@/models/device.models';
import { MediaMimeRootType } from '@/models/media';
import { RoiConfig } from '@/models/zone.models';
import { downloadMedia, getMediaEndpointUrl } from '@/utils/media.functions';
import { Download, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { MediaKindTabs } from './MediaKindTabs';
import SelectDeviceImageSlider from './SelectDeviceImageSlider';

type CompareDevicesPanelProps = {
  initialMediaKind: MediaKind;
  device: Device;
  timestamp?: string;
  displayPolygons?: boolean;
  onTimestampChange: (timestamp?: string) => void;
  onMediaKindChange: (mediaKind: MediaKind) => void;
  onRemoveClick: () => void;
  onPanelClick?: () => void;
};

export function CompareDevicesPanel({
  initialMediaKind,
  onMediaKindChange,
  device,
  displayPolygons,
  timestamp,
  onTimestampChange,
  onRemoveClick,
}: CompareDevicesPanelProps) {
  const [fetchedMedia, setFetchedMedia] = useState<Media | null>();
  const [mediaKind, setMediaKind] = useState<MediaKind>(initialMediaKind);
  const [mediaEndpointUrl, setMediaEndpointUrl] = useState<string>('');

  const polygonData: PolygonData[] = displayPolygons
    ? device?.rois?.map((roi) => {
        return {
          name: roi.name,
          polygon: (roi.config as RoiConfig).Points,
        };
      })
    : [];

  useEffect(() => {
    if (fetchedMedia === undefined) {
      return;
    }
    if (fetchedMedia === null) {
      setMediaEndpointUrl('');
      return;
    }

    const mediaEndpointUrl = getMediaEndpointUrl({
      mediaId: fetchedMedia.mediaId!,
      timestamp: fetchedMedia.time,
    });

    setMediaEndpointUrl(mediaEndpointUrl);
  }, [fetchedMedia]);

  const { mediaUrl, loading } = useFetchMedia({
    mediaEndpointUrl,
  });

  const handleDownloadClick = async () => {
    await downloadMedia({
      media: fetchedMedia,
      device,
    });
  };

  const handleImageChange = (image: Media | null) => {
    onTimestampChange(image?.time);
    setFetchedMedia(image);
  };

  const handleLiveImageChange = (image: Media | null) => {
    if (timestamp) {
      onTimestampChange(undefined);
    }
    setFetchedMedia(image);
  };

  const handleMediaKindChange = (kind: MediaKind) => {
    setMediaKind(kind);
    onMediaKindChange(kind);
  };

  return (
    <Card className="pt-4">
      <CardHeader>
        <CardTitle className="flex flex-row flex-wrap items-center justify-between gap-2">
          <Link
            to={`/devices/${device.id}`}
            className="text-base font-semibold underline-offset-4 hover:underline"
          >
            {device.name}
          </Link>

          <div className="flex flex-wrap gap-2">
            <MediaKindTabs
              mediaKind={mediaKind}
              setMediaKind={handleMediaKindChange}
            />
            <div className="flex gap-2">
              <Button
                variant={'outline'}
                size={'icon-sm'}
                onClick={() => void handleDownloadClick()}
                disabled={!fetchedMedia}
              >
                <Download />
              </Button>

              <Button
                variant={'outline'}
                size={'icon-sm'}
                onClick={onRemoveClick}
              >
                <X />
              </Button>
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-4">
          <div className="flex-1">
            <PolygonOverlay
              mediaUrl={mediaUrl ?? ''}
              polygonData={polygonData}
              loading={loading}
              isVideo={fetchedMedia?.mimeRootType === MediaMimeRootType.VIDEO}
            />
          </div>

          <SelectDeviceImageSlider
            timestamp={timestamp}
            device={device}
            mediaKind={mediaKind}
            onUserSelectedImageChange={handleImageChange}
            onLiveImageChange={handleLiveImageChange}
            setMediaKind={handleMediaKindChange}
          />
        </div>
      </CardContent>
    </Card>
  );
}
